// Exploding Kittens game logic utilities

/**
 * Initialize Exploding Kittens game (set up draw pile with exploding kittens)
 * @param {Array} fullDeck - Full shuffled deck (before dealing)
 * @param {Array} distributedCards - Cards already dealt to players (flattened)
 * @param {number} playerCount - Number of players
 * @returns {Object} - { drawPile, explodingKittensRemaining }
 */
export const initializeExplodingKittensGame = (fullDeck, distributedCards, playerCount) => {
  // Remove dealt cards from the deck
  const dealtCardIds = new Set(distributedCards.flat().map(c => c.id));
  const remainingDeck = fullDeck.filter(card => !dealtCardIds.has(card.id));
  
  // Separate exploding kittens from other cards
  const explodingKittens = remainingDeck.filter(c => c.type === 'exploding_kitten');
  const otherCards = remainingDeck.filter(c => c.type !== 'exploding_kitten');
  
  // Put exploding kittens back (number of players - 1, so one player will eventually win)
  const explodingKittensToAdd = explodingKittens.slice(0, Math.max(1, playerCount - 1));
  const drawPile = [...otherCards, ...explodingKittensToAdd];
  
  // Shuffle the draw pile thoroughly
  for (let i = drawPile.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [drawPile[i], drawPile[j]] = [drawPile[j], drawPile[i]];
  }
  
  return { drawPile, explodingKittensRemaining: explodingKittensToAdd.length };
};

/**
 * Draw a card from the draw pile
 * @param {Array} drawPile - Current draw pile
 * @returns {Object} - { card, newDrawPile, isExplodingKitten }
 */
export const drawCard = (drawPile) => {
  if (drawPile.length === 0) {
    return { card: null, newDrawPile: [], isExplodingKitten: false };
  }
  
  const card = drawPile[0];
  const newDrawPile = drawPile.slice(1);
  const isExplodingKitten = card.type === 'exploding_kitten';
  
  return { card, newDrawPile, isExplodingKitten };
};

/**
 * Place exploding kitten back in deck at specific position
 * @param {Array} drawPile - Current draw pile
 * @param {Object} explodingKitten - The exploding kitten card
 * @param {number} position - Position to insert (0 = top, drawPile.length = bottom)
 * @returns {Array} - New draw pile
 */
export const placeExplodingKitten = (drawPile, explodingKitten, position) => {
  const newDrawPile = [...drawPile];
  const insertPos = Math.max(0, Math.min(position, newDrawPile.length));
  newDrawPile.splice(insertPos, 0, explodingKitten);
  return newDrawPile;
};

/**
 * Handle action card effects
 * @param {Object} card - Action card played
 * @param {number} currentPlayerIndex - Current player index
 * @param {number} playerCount - Total number of players
 * @returns {Object} - { nextPlayerIndex, skipDraw, attackActive }
 */
export const handleActionCard = (card, currentPlayerIndex, playerCount) => {
  let nextPlayerIndex = currentPlayerIndex;
  let skipDraw = false;
  let attackActive = false;
  
  if (!card.action) return { nextPlayerIndex, skipDraw, attackActive };
  
  switch (card.action) {
    case 'skip':
      skipDraw = true;
      nextPlayerIndex = (currentPlayerIndex + 1) % playerCount;
      break;
      
    case 'attack':
      attackActive = true;
      skipDraw = true;
      // Next player must take 2 turns (implemented as skipDraw for current player)
      nextPlayerIndex = (currentPlayerIndex + 1) % playerCount;
      break;
      
    case 'see_future':
      // Show top 3 cards (handled in UI)
      nextPlayerIndex = currentPlayerIndex; // Don't advance turn
      break;
      
    case 'shuffle':
      // Shuffle draw pile (handled in backend)
      nextPlayerIndex = (currentPlayerIndex + 1) % playerCount;
      break;
      
    case 'favor':
      // Steal a card from another player (handled separately)
      nextPlayerIndex = currentPlayerIndex; // Don't advance until favor is completed
      break;
      
    case 'nope':
      // Cancel the last action (handled separately)
      nextPlayerIndex = currentPlayerIndex;
      break;
      
    default:
      nextPlayerIndex = (currentPlayerIndex + 1) % playerCount;
  }
  
  return { nextPlayerIndex, skipDraw, attackActive };
};

/**
 * Check if player can play a pair (two cards with same name/number)
 * @param {Array} selectedCards - Cards selected to play
 * @returns {boolean}
 */
export const canPlayPair = (selectedCards) => {
  if (selectedCards.length !== 2) return false;
  return selectedCards[0].name === selectedCards[1].name || 
         selectedCards[0].number === selectedCards[1].number;
};

/**
 * Check if player has defuse card
 * @param {Array} hand - Player's hand
 * @returns {boolean}
 */
export const hasDefuseCard = (hand) => {
  return hand && hand.some(card => card.type === 'defuse');
};

/**
 * Remove defuse card from hand
 * @param {Array} hand - Player's hand
 * @returns {Object} - { newHand, defuseCard }
 */
export const removeDefuseCard = (hand) => {
  const defuseIndex = hand.findIndex(card => card.type === 'defuse');
  if (defuseIndex === -1) return { newHand: hand, defuseCard: null };
  
  const defuseCard = hand[defuseIndex];
  const newHand = hand.filter((_, index) => index !== defuseIndex);
  return { newHand, defuseCard };
};
